import React, { useState, useEffect, useCallback } from 'react';
import { getCommunity, getCommunityMembers, isMember, joinCommunity, leaveCommunity, getCommunityEventCache, publishChannelCreate, deleteCommunity, publishModGrant, publishModRevoke, publishCommunityBan, publishCommunityUnban, publishCommunityLock, publishCommunityUnlock } from '../lib/communities';
import { publishEvent } from '../lib/sync';
import { getCommunityFreshFeed, getCommunityPopularFeed, getCommunityRisingFeed } from '../lib/communityFeeds';
import { loadOrCreateIdentity } from '../lib/identity';
import { getProfile, getRemoteProfile } from '../lib/metadata';
import { asText, pubkeyShortHash } from '../lib/sanitize';
import { deriveCommunityState } from '../lib/communityState';
import { ChannelView } from './ChannelView';
import { VideoPlayer } from './VideoPlayer';
import { COLORS, SPACING } from '../styles/theme';

function formatDisplayName(userId: string): string {
  const profile = getRemoteProfile(userId);
  const name = asText(profile?.username || userId);
  return `${name} [${pubkeyShortHash(profile?.publicKey || userId)}]`;
}

export function CommunityDetail({ communityId, onBack, onViewUserProfile, onViewVideo }: { communityId: string; onBack: () => void; onViewUserProfile: (userId: string) => void; onViewVideo: (hash: string) => void }) {
  const [community, setCommunity] = useState<any>(null);
  const [feedType, setFeedType] = useState<'fresh' | 'rising' | 'popular'>('fresh');
  const [videos, setVideos] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [identity, setIdentity] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [isMemberBool, setIsMember] = useState(false);
  const [activeTab, setActiveTab] = useState<'feed' | 'channels' | 'members' | 'settings'>('feed');
  const [activeChannel, setActiveChannel] = useState<{ id: string; name: string } | null>(null);
  const [selectedVideoHash, setSelectedVideoHash] = useState<string | null>(null);
  const [newChannelName, setNewChannelName] = useState('');
  const [creatingChannel, setCreatingChannel] = useState(false);

  const loadFeed = useCallback(() => {
    let feed;
    if (feedType === 'fresh') feed = getCommunityFreshFeed(communityId);
    else if (feedType === 'rising') feed = getCommunityRisingFeed(communityId);
    else feed = getCommunityPopularFeed(communityId);
    setVideos(feed);
  }, [feedType, communityId]);

  useEffect(() => {
    const id = loadOrCreateIdentity();
    setIdentity(id);
    setProfile(getProfile());
    setCommunity(getCommunity(communityId));
    setMembers(getCommunityMembers(communityId));
    setIsMember(isMember(communityId, id.userId));
    loadFeed();
  }, [communityId, loadFeed]);

  const handleJoin = () => {
    if (!identity || !profile) return;
    joinCommunity(communityId, identity.userId, profile.username || identity.userId);
    setIsMember(true);
    setMembers(getCommunityMembers(communityId));
  };

  const handleLeave = () => {
    if (!identity) return;
    leaveCommunity(communityId, identity.userId);
    setIsMember(false);
    onBack();
  };

  const handleDeleteCommunity = async () => {
    if (!identity || !community) return;
    if (!window.confirm('Delete this community? This cannot be undone.')) return;
    deleteCommunity(community.id, identity.userId);
    await publishEvent('delete_community', { communityId: community.id, userId: identity.userId });
    onBack();
  };

  if (!community) return <div style={{ padding: SPACING.lg, color: COLORS.textMuted, fontFamily: 'monospace', fontSize: '10px' }}>Loading...</div>;

  if (selectedVideoHash) {
    return <VideoPlayer videoHash={selectedVideoHash} onBack={() => setSelectedVideoHash(null)} onViewUserProfile={onViewUserProfile} onViewTag={() => {}} />;
  }

  const tabStyle = (t: string) => ({
    padding: `${SPACING.sm} ${SPACING.md}`,
    background: 'transparent',
    border: 'none',
    borderBottom: activeTab === t ? `2px solid ${COLORS.accent}` : '2px solid transparent',
    color: activeTab === t ? COLORS.text : COLORS.textMuted,
    cursor: 'pointer',
    fontFamily: 'monospace',
    fontSize: '11px',
    fontWeight: activeTab === t ? 700 : 400,
    textTransform: 'capitalize' as const,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', border: `2px outset ${COLORS.highlight}`, backgroundColor: COLORS.panel, overflow: 'hidden' }}>
      {/* Title bar */}
      <div style={{ backgroundColor: COLORS.accent, color: '#fff', padding: `${SPACING.xs} ${SPACING.sm}`, fontSize: '9px', fontWeight: 700, borderBottom: `2px solid ${COLORS.highlight}`, userSelect: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{community.name}</span>
        <button onClick={onBack} style={{ backgroundColor: 'transparent', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}>✕</button>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.border}`, backgroundColor: COLORS.bg, flexShrink: 0 }}>
        {(['feed', 'channels', 'members', 'settings'] as const).map(t => (
          <button key={t} onClick={() => { setActiveTab(t); setActiveChannel(null); }} style={tabStyle(t)}>{t}</button>
        ))}
      </div>

      {/* Feed tab */}
      {activeTab === 'feed' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: `${SPACING.xs} ${SPACING.sm}`, borderBottom: `1px solid ${COLORS.border}`, backgroundColor: COLORS.bg, display: 'flex', gap: SPACING.sm, flexShrink: 0 }}>
            {(['fresh', 'rising', 'popular'] as const).map(type => (
              <button key={type} onClick={() => setFeedType(type)}
                style={{ padding: `${SPACING.xs} ${SPACING.sm}`, background: feedType === type ? COLORS.accent : 'transparent', color: feedType === type ? '#fff' : COLORS.textMuted, border: 'none', cursor: 'pointer', fontFamily: 'monospace', fontSize: '10px', fontWeight: feedType === type ? 700 : 400, textTransform: 'capitalize' }}>
                {type}
              </button>
            ))}
          </div>
          <div style={{ flex: 1, overflowY: 'auto', backgroundColor: COLORS.bg }}>
            {!isMemberBool ? (
              <p style={{ padding: SPACING.lg, color: COLORS.textMuted, margin: 0, fontSize: '10px', fontFamily: 'monospace' }}>Join to view videos</p>
            ) : videos.length === 0 ? (
              <p style={{ padding: SPACING.lg, color: COLORS.textMuted, margin: 0, fontSize: '10px', fontFamily: 'monospace' }}>No videos yet</p>
            ) : (
              videos.map((item: any) => (
                <div key={item.videoHash} onClick={() => setSelectedVideoHash(item.videoHash)}
                  style={{ padding: SPACING.lg, borderBottom: `1px solid ${COLORS.border}`, cursor: 'pointer' }}>
                  <div style={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 700, color: COLORS.accent, marginBottom: SPACING.xs }}>{item.video?.title}</div>
                  <div style={{ fontSize: '9px', color: COLORS.textMuted, fontFamily: 'monospace' }}>
                    <button onClick={e => { e.stopPropagation(); onViewUserProfile(item.pinnedBy); }}
                      style={{ background: 'transparent', border: 'none', color: COLORS.accent, cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit', fontFamily: 'inherit', padding: 0 }}>
                      {formatDisplayName(item.pinnedBy)}
                    </button>
                    {' · '}{new Date(item.pinnedAt).toLocaleDateString()}
                  </div>
                  {item.video?.description && <div style={{ fontSize: '9px', color: COLORS.text, fontFamily: 'monospace', marginTop: SPACING.xs }}>{item.video.description.slice(0, 80)}</div>}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Channels tab */}
      {activeTab === 'channels' && (
        activeChannel ? (
          <ChannelView communityId={communityId} channelId={activeChannel.id} channelName={activeChannel.name} onBack={() => setActiveChannel(null)} />
        ) : (() => {
          const events = getCommunityEventCache();
          const state = deriveCommunityState(communityId, events);
          let derivedChannels: Array<{ id: string; name: string; createdAt: number }>;
          if (state) {
            derivedChannels = state.channels;
          } else {
            const channelEvents = events.filter(
              e => e.type === 'channel_create' && (e.payload as any).communityId === communityId
            );
            const extra = channelEvents.map(e => ({
              id: (e.payload as any).channelId as string,
              name: (e.payload as any).channelName as string,
              createdAt: e.timestamp,
            }));
            derivedChannels = [{ id: 'general', name: 'general', createdAt: 0 }, ...extra];
          }
          const isOwner = community.creatorId === identity?.userId;

          async function handleCreateChannel() {
            const name = newChannelName.trim();
            if (!name) return;
            const channelId = Date.now().toString(36);
            setCreatingChannel(true);
            await publishChannelCreate(communityId, channelId, name);
            setNewChannelName('');
            setCreatingChannel(false);
          }

          return (
            <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.lg, backgroundColor: COLORS.bg, display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
              {derivedChannels.map(ch => (
                <button key={ch.id} onClick={() => setActiveChannel(ch)}
                  style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, color: COLORS.text, padding: SPACING.sm, cursor: 'pointer', fontFamily: 'monospace', fontSize: '12px', textAlign: 'left' }}>
                  # {ch.name}
                </button>
              ))}
              {isOwner && (
                <div style={{ display: 'flex', gap: SPACING.sm, marginTop: SPACING.sm }}>
                  <input
                    value={newChannelName}
                    onChange={e => setNewChannelName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleCreateChannel(); }}
                    placeholder="new-channel"
                    style={{ flex: 1, fontFamily: 'monospace', fontSize: '12px', background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}`, padding: SPACING.xs }}
                  />
                  <button onClick={handleCreateChannel} disabled={!newChannelName.trim() || creatingChannel}
                    style={{ background: COLORS.accent, color: '#fff', border: 'none', padding: `${SPACING.xs} ${SPACING.sm}`, cursor: 'pointer', fontFamily: 'monospace', fontSize: '12px', fontWeight: 700 }}>
                    + channel
                  </button>
                </div>
              )}
            </div>
          );
        })()
      )}

      {/* Members tab */}
      {activeTab === 'members' && (() => {
        const events = getCommunityEventCache();
        const state = deriveCommunityState(communityId, events);
        const myPubkey = identity?.publicKey ?? '';
        const isOwner = state ? myPubkey === state.ownerPubkey : community.creatorId === identity?.userId;
        const isMod = state ? state.mods.has(myPubkey) : false;
        const canModerate = isOwner || isMod;

        const handleBan = async (targetUserId: string) => {
          const prof = getRemoteProfile(targetUserId);
          if (!prof?.publicKey) return;
          await publishCommunityBan(communityId, prof.publicKey);
          setMembers(getCommunityMembers(communityId));
        };
        const handleGrantMod = async (targetUserId: string) => {
          const prof = getRemoteProfile(targetUserId);
          if (!prof?.publicKey) return;
          await publishModGrant(communityId, prof.publicKey);
        };
        const handleRevokeMod = async (targetUserId: string) => {
          const prof = getRemoteProfile(targetUserId);
          if (!prof?.publicKey) return;
          await publishModRevoke(communityId, prof.publicKey);
        };

        return (
          <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.lg, backgroundColor: COLORS.bg }}>
            <p style={{ margin: `0 0 ${SPACING.md} 0`, fontSize: '9px', color: COLORS.textMuted, fontFamily: 'monospace' }}>{members.length} members</p>
            {members.map(m => {
              const prof = getRemoteProfile(m.userId);
              const pubkey = prof?.publicKey ?? '';
              const memberIsMod = state?.mods.has(pubkey) ?? false;
              const memberIsOwner = state ? pubkey === state.ownerPubkey : m.userId === community.creatorId;
              const isSelf = m.userId === identity?.userId;
              return (
                <div key={m.userId} style={{ padding: `${SPACING.sm} 0`, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '10px', fontFamily: 'monospace', color: COLORS.text, flex: 1 }}>
                    {formatDisplayName(m.userId)}
                    {memberIsOwner && <span style={{ marginLeft: 6, fontSize: '9px', color: COLORS.accent, fontWeight: 700 }}>owner</span>}
                    {memberIsMod && !memberIsOwner && <span style={{ marginLeft: 6, fontSize: '9px', color: COLORS.highlight, fontWeight: 700 }}>mod</span>}
                  </span>
                  {canModerate && !isSelf && !memberIsOwner && (
                    <>
                      {isOwner && (
                        <button
                          onClick={() => memberIsMod ? handleRevokeMod(m.userId) : handleGrantMod(m.userId)}
                          style={{ background: 'none', border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, padding: '1px 7px', cursor: 'pointer', fontFamily: 'monospace', fontSize: '9px' }}
                        >
                          {memberIsMod ? 'revoke mod' : 'make mod'}
                        </button>
                      )}
                      <button
                        onClick={() => handleBan(m.userId)}
                        style={{ background: 'none', border: `1px solid ${COLORS.danger}`, color: COLORS.danger, padding: '1px 7px', cursor: 'pointer', fontFamily: 'monospace', fontSize: '9px' }}
                      >
                        ban
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Settings tab */}
      {activeTab === 'settings' && (() => {
        const events = getCommunityEventCache();
        const state = deriveCommunityState(communityId, events);
        const myPubkey = identity?.publicKey ?? '';
        const isOwner = state ? myPubkey === state.ownerPubkey : community.creatorId === identity?.userId;
        const isMod = state ? state.mods.has(myPubkey) : false;
        const canModerate = isOwner || isMod;
        const isLocked = state?.locked ?? false;

        return (
          <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.lg, backgroundColor: COLORS.bg }}>
            <p style={{ fontSize: '11px', color: COLORS.text, fontFamily: 'monospace', lineHeight: 1.6, marginBottom: SPACING.lg }}>{community.description}</p>
            <p style={{ fontSize: '9px', color: COLORS.textMuted, fontFamily: 'monospace', marginBottom: SPACING.lg }}>Created {new Date(community.createdAt).toLocaleDateString()}</p>
            <div style={{ display: 'flex', gap: SPACING.sm, flexWrap: 'wrap' }}>
              {isMemberBool ? (
                <button onClick={handleLeave}
                  style={{ background: 'transparent', border: `1px solid ${COLORS.danger}`, color: COLORS.danger, padding: `${SPACING.sm} ${SPACING.lg}`, cursor: 'pointer', fontFamily: 'monospace', fontSize: '11px', fontWeight: 700 }}>
                  Leave community
                </button>
              ) : (
                <button onClick={handleJoin}
                  style={{ background: COLORS.accent, border: 'none', color: '#fff', padding: `${SPACING.sm} ${SPACING.lg}`, cursor: 'pointer', fontFamily: 'monospace', fontSize: '11px', fontWeight: 700 }}>
                  Join community
                </button>
              )}
              {canModerate && (
                <button
                  onClick={() => isLocked ? publishCommunityUnlock(communityId) : publishCommunityLock(communityId)}
                  style={{ background: 'none', border: `1px solid ${COLORS.border}`, color: COLORS.text, padding: `${SPACING.sm} ${SPACING.lg}`, cursor: 'pointer', fontFamily: 'monospace', fontSize: '11px' }}
                >
                  {isLocked ? '🔓 unlock' : '🔒 lock'}
                </button>
              )}
              {isOwner && (
                <button onClick={handleDeleteCommunity}
                  style={{ background: COLORS.danger, border: 'none', color: '#fff', padding: `${SPACING.sm} ${SPACING.lg}`, cursor: 'pointer', fontFamily: 'monospace', fontSize: '11px', fontWeight: 700 }}>
                  🗑 Delete community
                </button>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

